import valid from 'card-validator'

// ============================================================================
// Types
// ============================================================================

export type CardType = 'visa' | 'mastercard' | 'jcb' | 'american-express' | ''

export type SupportedCountry = 'TH' | 'JP' | 'AU' | 'NZ' | 'SA' | 'SG' | 'MY' | 'KW'

export interface CreditCardInfo {
    creditCardNumber: string
    payer: string
    creditCardExpires: string
    creditCardSecurityCode: string
}

export interface CardValidationInput {
    countryCode: string
    creditCard: CreditCardInfo
}

export interface CardValidationResult {
    success: boolean
    error_messages: string[]
}

interface ValidationContext {
    cardType: CardType | undefined
    errors: string[]
    success: boolean
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Pushes an error to the validation context
 */
function pushError(context: ValidationContext, errorMsg: string): void {
    context.errors.push(errorMsg)
    context.success = false
}

/**
 * Validates credit card number
 */
function validateNumber(context: ValidationContext, cardNumber: string): void {
    if (!cardNumber) {
        pushError(context, 'creditcard_not_found')
        return
    }

    const numberValidation = valid.number(cardNumber)
    if (!numberValidation.isValid || !/^[0-9]*$/g.test(cardNumber)) {
        pushError(context, 'creditcard_invalid')
    }

    context.cardType = numberValidation?.card?.type as CardType | undefined
}

/**
 * Validates cardholder name
 */
function validatePayer(context: ValidationContext, payer: string): void {
    if (payer === '') {
        pushError(context, 'cardholder_not_found')
        return
    }

    // Match: a-z, A-Z, space, apostrophe, hyphen
    if (!/^[a-zA-Z]+([ '-][a-zA-Z]+)*$/g.test(payer)) {
        pushError(context, 'cardholder_invalid')
    }
}

/**
 * Validates expiration date (format: MM/YY)
 */
function validateExpirationDate(context: ValidationContext, expiredDate: string): void {
    if (expiredDate === undefined) {
        pushError(context, 'expired_not_found')
        return
    }

    const { isValid } = valid.expirationDate(expiredDate)
    if (!isValid || !/^\d{2}\/\d{2}$/g.test(expiredDate)) {
        pushError(context, 'expired_invalid')
    }
}

/**
 * Validates country code and card type support
 */
function validateCountry(context: ValidationContext, countryCode: string): void {
    if (countryCode === undefined) {
        pushError(context, 'country_code_not_found')
        return
    }

    const supportedCountries: SupportedCountry[] = ['TH', 'JP', 'AU', 'NZ', 'SA', 'SG', 'MY', 'KW']
    if (!supportedCountries.includes(countryCode as SupportedCountry)) {
        pushError(context, 'country_code_is_not_support')
    }

    if (!context.cardType) {
        return
    }

    // Country-specific card type validation
    if (['TH', 'AU', 'NZ'].includes(countryCode)) {
        const acceptedCards: CardType[] = ['visa', 'mastercard', 'jcb']
        if (!acceptedCards.includes(context.cardType)) {
            pushError(context, 'only_accept_visa_or_mastercard')
        }
    }

    if (countryCode === 'JP') {
        const acceptedCards: CardType[] = ['visa', 'mastercard', 'american-express', 'jcb']
        if (!acceptedCards.includes(context.cardType)) {
            pushError(context, `jp_not_accept_${context.cardType}`)
        }
    }
}

/**
 * Validates CVV/CVC security code
 */
function validateCVV(context: ValidationContext, cvv: string): void {
    if (!cvv) {
        pushError(context, 'securitycode_not_found')
        return
    }

    const cvvLength = context.cardType === 'american-express' ? 4 : 3
    const { isValid } = valid.cvv(cvv, cvvLength)
    if (!isValid) {
        pushError(context, 'securitycode_invalid')
    }
}

/**
 * Validates that card type is supported
 */
function validateCardType(context: ValidationContext): void {
    const acceptedCards: CardType[] = ['visa', 'mastercard', 'jcb', 'american-express']
    if (!context.cardType || !acceptedCards.includes(context.cardType)) {
        pushError(context, 'only_accept_visa_or_mastercard')
    }
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Validates a credit card for processing
 *
 * @example
 * ```ts
 * const result = cardValidate({
 *   countryCode: 'JP',
 *   creditCard: {
 *     creditCardNumber: '4111111111111111',
 *     payer: 'John Doe',
 *     creditCardExpires: '12/25',
 *     creditCardSecurityCode: '123'
 *   }
 * })
 *
 * if (!result.success) {
 *   console.log(result.error_messages)
 * }
 * ```
 *
 * @param cardInfo - Card information to validate
 * @returns Validation result with success status and error messages
 */
function cardValidate(cardInfo: CardValidationInput): CardValidationResult {
    const context: ValidationContext = {
        cardType: undefined,
        errors: [],
        success: true
    }

    const { countryCode } = cardInfo
    const { creditCardNumber, payer, creditCardExpires, creditCardSecurityCode } = cardInfo.creditCard

    validateNumber(context, creditCardNumber)
    validatePayer(context, payer)
    validateExpirationDate(context, creditCardExpires)
    validateCountry(context, countryCode)
    validateCVV(context, creditCardSecurityCode)
    validateCardType(context)

    return {
        success: context.success,
        error_messages: context.errors
    }
}

/**
 * Validates if a card number is JCB
 */
function validateJCB(cardNumber: string): boolean {
    const jcbRegex = /^3[0-9]/
    return jcbRegex.test(cardNumber)
}

/**
 * Detects card type from card number
 *
 * @example
 * ```ts
 * checkCardType('4111111111111111') // 'visa'
 * checkCardType('5500000000000004') // 'mastercard'
 * checkCardType('378282246310005')  // 'american-express'
 * ```
 *
 * @param cardNumber - The card number to check
 * @param full - If true, validates full card number; if false, validates partial
 * @returns The detected card type or empty string
 */
function checkCardType(cardNumber: string, full = true): CardType {
    if (full) {
        // Visa: starts with 4, 13-16 digits
        if (/^4[0-9]{12}(?:[0-9]{3})?$/.test(cardNumber)) {
            return 'visa'
        }

        // Mastercard: starts with 51-55 or 2221-2720, 16 digits
        if (/^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/.test(cardNumber)) {
            return 'mastercard'
        }

        // American Express: starts with 34 or 37, 15 digits
        if (/^3[47][0-9]{13}$/.test(cardNumber)) {
            return 'american-express'
        }

        // JCB
        if (validateJCB(cardNumber)) {
            return 'jcb'
        }

        return ''
    }

    // Partial validation (for real-time detection)
    if (/^4/.test(cardNumber)) return 'visa'
    if (/^5[1-5]/.test(cardNumber)) return 'mastercard'
    if (/^3[47]/.test(cardNumber)) return 'american-express'
    if (validateJCB(cardNumber)) return 'jcb'

    return ''
}

export const CreditCardHelper = {
    checkCardType,
    cardValidate
}
